"use client";

import { cn } from "@frontend/shared/lib/utils";
import { isValid, parse } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { formatRequiredLabel } from "./IntakeFormQuestion";

interface DateInputGroupProps {
  month: string;
  day: string;
  year: string;
  onMonthChange: (value: string) => void;
  onDayChange: (value: string) => void;
  onYearChange: (value: string) => void;
  error?: boolean;
  className?: string;
  innerClassName?: string;
}

export const DateInputGroup = ({
  month,
  day,
  year,
  onMonthChange,
  onDayChange,
  onYearChange,
  error = false,
  className,
  innerClassName,
}: DateInputGroupProps) => {
  const monthInputRef = useRef<HTMLInputElement>(null);
  const dayInputRef = useRef<HTMLInputElement>(null);
  const yearInputRef = useRef<HTMLInputElement>(null);

  const smartPad = (value: string) => {
    return value.toString().length === 1 && value !== "0"
      ? value.toString().padStart(2, "0")
      : value;
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onMonthChange(e.target.value);
    setTimeout(() => {
      if (e.target.value.length === 2) {
        dayInputRef.current?.focus();
      }
    }, 100);
  };

  const handleDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onDayChange(e.target.value);
    setTimeout(() => {
      if (e.target.value.length === 2) {
        yearInputRef.current?.focus();
      }
    }, 100);
  };

  const classNames =
    "w-1/3 rounded-full border-transparent bg-gray-100 p-3 m-1 focus:outline-none focus:ring-0 transition-all duration-300 placeholder:text-gray-400 font-light placeholder:font-light";
  const filledClassNames = "border bg-white border-black";

  return (
    <div className={cn("flex gap-2 items-center", className)}>
      <input
        ref={monthInputRef}
        type="number"
        maxLength={2}
        value={month}
        placeholder="MM"
        className={cn(
          classNames,
          month && filledClassNames,
          error && "border-red-500",
          innerClassName,
        )}
        onChange={(e) => {
          if (e.target.value.length <= 2) {
            handleMonthChange(e);
          }
        }}
        onBlur={() => {
          if (month.length === 1 && month !== "0") {
            onMonthChange(month.toString().padStart(2, "0"));
          }
        }}
      />
      <p className="text-gray-300 text-3xl font-thin">/</p>
      <input
        ref={dayInputRef}
        type="number"
        maxLength={2}
        value={day}
        placeholder="DD"
        className={cn(
          classNames,
          day && filledClassNames,
          error && "border-red-500",
          innerClassName,
        )}
        onChange={(e) => {
          if (e.target.value.length <= 2) {
            handleDayChange(e);
          }
        }}
        onBlur={() => {
          if (day.length === 1 && day !== "0") {
            onDayChange(day.toString().padStart(2, "0"));
          }
        }}
      />
      <p className="text-gray-300 text-3xl font-thin">/</p>
      <input
        ref={yearInputRef}
        type="number"
        maxLength={4}
        value={year}
        placeholder="YYYY"
        className={cn(
          classNames,
          year && filledClassNames,
          error && "border-red-500",
          innerClassName,
        )}
        onChange={(e) => {
          if (e.target.value.length <= 4) {
            onYearChange(e.target.value);
          }
          if (e.target.value.length === 4) {
            onYearChange(e.target.value);
            yearInputRef.current?.blur();
          }
        }}
      />
    </div>
  );
};

export const IntakeDateInput = ({
  field,
  setValue,
  error,
  questionText,
  isRequired,
  clearErrors,
  hideQuestionText,
}) => {
  const formattedLabel = formatRequiredLabel(
    questionText,
    isRequired,
    hideQuestionText,
  );
  const prefilledDate = field.value
    ? parse(field.value, "yyyy-MM-dd", new Date())
    : null;
  const prefilledMonth = prefilledDate ? prefilledDate.getMonth() + 1 : "";
  const prefilledDay = prefilledDate ? prefilledDate.getDate() : "";
  const prefilledYear = prefilledDate ? prefilledDate.getFullYear() : "";

  const smartPad = (value: string) => {
    return value.toString().length === 1 && value !== "0"
      ? value.toString().padStart(2, "0")
      : value;
  };

  const [month, setMonth] = useState(smartPad(prefilledMonth.toString()));
  const [day, setDay] = useState(smartPad(prefilledDay.toString()));
  const [year, setYear] = useState(smartPad(prefilledYear.toString()));

  useEffect(() => {
    if (!month && !day && !year) {
      setValue(field.name, "");
      clearErrors(field.name);
      return;
    }
    const builtDate = `${year}-${month}-${day}`;
    if (isValid(parse(builtDate, "yyyy-MM-dd", new Date()))) {
      clearErrors(field.name);
      setValue(field.name, builtDate);
    }
  }, [month, day, year, setValue, field.name, clearErrors]);

  return (
    <>
      <p className="text-sm">{formattedLabel}</p>
      <DateInputGroup
        month={month}
        day={day}
        year={year}
        onMonthChange={setMonth}
        onDayChange={setDay}
        onYearChange={setYear}
        error={!!error}
      />
    </>
  );
};
